import React from 'react';
import { Calendar, ArrowRight, Lock, Layers } from 'lucide-react';
import { ListingTemplate } from '../../types';

interface ListingTemplateCardProps {
  template: ListingTemplate;
  onClick: () => void;
}

export const ListingTemplateCard: React.FC<ListingTemplateCardProps> = ({
  template,
  onClick,
}) => {
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return formatDate(date);
  };

  return (
    <div 
      onClick={onClick}
      className="relative bg-white/98 dark:bg-dark-surface backdrop-blur-sm rounded-xl 
        border border-modular-stone-alloy/40 dark:border-dark-border p-6 hover:shadow-modular-card 
        dark:hover:shadow-dark-elevated transition-all duration-300 group 
        hover:border-modular-frosted-indigo dark:hover:border-modular-frosted-indigo transform hover:scale-[1.02] 
        cursor-pointer min-h-[280px] flex flex-col shadow-sm dark:shadow-dark-card"
    >
      {/* Image Indicator Badge */}
      {template.imageUrl && (
        <div className="absolute top-2 right-2 bg-fire-100 dark:bg-fire-900/30 text-fire-800 dark:text-fire-300 
          px-2 py-1 rounded-full text-xs border border-fire-200 dark:border-fire-800 flex items-center">
          <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Image
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text 
            group-hover:text-modular-frosted-indigo dark:group-hover:text-modular-frosted-indigo transition-colors duration-300
            line-clamp-2 leading-tight mb-2">
            {template.name}
          </h3>
          
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-3">
            <Calendar className="h-4 w-4 mr-1 flex-shrink-0" />
            <span className="whitespace-nowrap">{formatRelativeTime(template.createdAt)}</span>
          </div>
        </div>
        
        <ArrowRight className="h-5 w-5 text-gray-400 dark:text-gray-500 
          group-hover:text-fire-500 dark:group-hover:text-fire-400 
          group-hover:translate-x-1 transition-all duration-300 flex-shrink-0 ml-3" />
      </div>

      {/* Master Title */}
      <div className="mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 font-medium">
          Master Title:
        </p>
        <p className="text-gray-900 dark:text-dark-text font-medium line-clamp-2">
          {template.masterTitle}
        </p>
      </div>

      {/* Master Description Preview */}
      <div className="flex-1 mb-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2 font-medium">
          Description:
        </p>
        <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed line-clamp-3">
          {template.masterDescription}
        </p>
      </div>

      {/* Locked Elements */}
      {template.lockedElements.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <Lock className="h-4 w-4 text-emerald-500 dark:text-emerald-400 mr-1" />
            <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
              {template.lockedElements.length} locked element{template.lockedElements.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex flex-wrap gap-1">
            {template.lockedElements.slice(0, 2).map((element, index) => (
              <div
                key={index}
                className="inline-flex items-center px-2 py-1 rounded text-xs 
                  bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 
                  border border-emerald-200 dark:border-emerald-800"
              >
                <Lock className="h-3 w-3 mr-1" />
                <span className="max-w-[80px] truncate">{element}</span>
              </div>
            ))}
            {template.lockedElements.length > 2 && (
              <div className="inline-flex items-center px-2 py-1 rounded text-xs 
                bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                +{template.lockedElements.length - 2} more
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mt-auto">
        <div className="flex items-center">
          <Layers className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="whitespace-nowrap">Master Template</span>
        </div>

        <div className="flex items-center text-modular-frosted-indigo dark:text-modular-frosted-indigo font-medium">
          <span className="text-xs">Generate Variations</span>
        </div>
      </div>
    </div>
  );
};